/**
 * The X window — which of the loaded bars are on screen, and what it costs to
 * change that.
 *
 * **Loaded and visible are different numbers**, and having only one of them is
 * what made a chart feel fixed: `ChartGroup.barsRange` is what the server sends
 * and `ChartGroup.visibleBars` is what the axis shows. 150 visible against 1500
 * loaded is ten screens of pannable history for one request. Neither is the
 * other's business — moving the window must never refetch, and loading more must
 * never move the window.
 *
 * Everything here is index arithmetic, because the contract's X axis is a
 * **category** axis (`xAxis: { type: "category" }`): it collapses non-trading
 * gaps, so a window is a pair of bar indices and nothing on that axis knows what
 * a Tuesday is. "Go to a date" is therefore two steps — find the bar, then move
 * the window — and the first of them is the binary search below.
 *
 * Pure and tested here rather than inside the view, for the reason
 * `lib/layout-view.ts` and `lib/active-section.ts` are: SciChart cannot load in
 * jsdom, so anything reaching the chart is out of the suite's reach, and this is
 * the part with the edge cases.
 */

/**
 * An X window in bar indices.
 *
 * `max` sits *past* the last visible bar by the right margin, which is why this
 * is not simply a pair of bar indices: the margin is empty space measured in the
 * same units, and folding it into `max` keeps one number for the renderer to
 * apply instead of a range plus a correction it has to remember to make.
 */
export interface BarWindow {
  min: number;
  max: number;
}

/**
 * Bars on screen when a chart says nothing.
 *
 * 150 against a 1500-bar load, which is the ratio the whole feature is for.
 */
export const DEFAULT_VISIBLE_BARS = 150;

/** No empty space past the last bar unless a chart asks for some. */
export const DEFAULT_RIGHT_MARGIN = 0;

/**
 * The most bars one request will return — `MAX_BARS` in qv-data's config.
 *
 * Duplicated deliberately, and it is the one number here that is a copy of
 * something. A preset has to be able to say "that would need 22,000 bars and you
 * cannot have them" *before* it asks, and a control that only learns its limit
 * from the reply is a control that promises a year and then shows two months.
 * The server still clamps; this only lets the button be honest first.
 */
export const MAX_LOADED_BARS = 5000;

/** A chart group's window settings, with the contract's defaults filled in. */
export function visibleBarsOf(group: { visibleBars?: number }): number {
  const n = group.visibleBars;
  return typeof n === "number" && n >= 1 ? Math.floor(n) : DEFAULT_VISIBLE_BARS;
}

/** The empty fraction kept to the right of the last bar. */
export function rightMarginOf(group: { rightMargin?: number }): number {
  const m = group.rightMargin;
  return typeof m === "number" && m > 0 ? m : DEFAULT_RIGHT_MARGIN;
}

/**
 * How wide a window showing `visibleBars` bars is, margin included.
 *
 * `visibleBars - 1` because a window from index 0 to index 149 shows 150 bars:
 * the range is inclusive of both ends. Off by one here is off by one bar at
 * every zoom level, which reads as the count box being wrong rather than as
 * arithmetic.
 */
function windowWidth(visibleBars: number, rightMargin: number): number {
  return visibleBars - 1 + visibleBars * rightMargin;
}

/**
 * The window whose rightmost bar is `lastBar`.
 *
 * `min` is **not clamped at zero**. Asked to show more bars than have loaded,
 * this leaves empty space on the left rather than quietly narrowing the window —
 * which would make the count box say 300 while 100 bars were on screen. The gap
 * is the honest answer, and it is also the visible edge of the history.
 */
export function windowEndingAt(
  lastBar: number,
  visibleBars: number,
  rightMargin = DEFAULT_RIGHT_MARGIN,
): BarWindow {
  const max = lastBar + visibleBars * rightMargin;
  return { min: max - windowWidth(visibleBars, rightMargin), max };
}

/**
 * The window `visibleBars` wide whose rightmost **bar** stays where it was.
 *
 * This is what changing the count box does, and the rejected alternative is the
 * obvious one: re-anchoring to the latest bar. Widening from 150 to 300 while
 * reading 2021 would then jump four years forward, so asking for *more history*
 * would move you away from the history you were looking at. Growing leftwards is
 * what the gesture means.
 *
 * **The bar, not the edge**, and `appliedGap` is what tells them apart: the
 * current window's `max` sits past its last bar by whatever margin it was drawn
 * with, so holding `max` and re-deriving from the *new* margin would keep the
 * empty space and add the difference to the left instead. Turning a right margin
 * on then quietly bought bars on the left and produced no margin at all —
 * measured on a running chart, which is the only place it is visible.
 *
 * @param appliedGap Empty index space in the current window — `visibleBars *
 * rightMargin` as it was when that window was written.
 */
export function windowHoldingRight(
  current: BarWindow,
  appliedGap: number,
  visibleBars: number,
  rightMargin = DEFAULT_RIGHT_MARGIN,
): BarWindow {
  return windowEndingAt(current.max - appliedGap, visibleBars, rightMargin);
}

/** The empty index space a window of this shape carries past its last bar. */
export function rightGap(visibleBars: number, rightMargin: number): number {
  return visibleBars * rightMargin;
}

/**
 * The fewest bars the zoom buttons will leave on screen.
 *
 * Ten is already a chart of individual candles; below that a "chart" is a
 * handful of bars and the next click would leave one.
 */
export const MIN_VISIBLE_BARS = 10;

/**
 * How many bars a window that is already on the axis is showing.
 *
 * The inverse of {@link windowEndingAt}'s width — `visibleBars - 1` between the
 * first bar and the last, plus the margin's empty space — so a range the
 * *wheel* produced can be measured rather than assumed. The wheel is a
 * `MouseWheelZoomModifier` on the axis and never writes `visibleBars`, so the
 * stored count and what is on screen part company the moment anyone scrolls.
 *
 * Rounded, and never below one: a fractional count is not a number of bars, and
 * a window narrower than a bar is a chart nobody can read.
 */
export function barsInWindow(window: BarWindow, rightMargin = DEFAULT_RIGHT_MARGIN): number {
  return Math.max(1, Math.round((window.max - window.min + 1) / (1 + rightMargin)));
}

/**
 * How far one press of ‹ or › moves the window: a quarter of what is on screen.
 *
 * A share rather than a bar count, for `rightMargin`'s reason — twenty bars is a
 * third of a zoomed-in chart and a twitch on a zoomed-out one — and a quarter
 * rather than a half so the bars you were reading are still on screen after the
 * press, which is what makes repeated presses feel like scrolling rather than
 * like paging.
 */
export function panStep(visibleBars: number): number {
  return Math.max(1, Math.round(visibleBars / 4));
}

/**
 * The window moved `deltaBars` along, the same width, clamped at both ends.
 *
 * **Its width never changes**, which is what separates panning from every other
 * control here: the count box and the zoom buttons change how much is on screen,
 * and this changes only which part of the history that is.
 *
 * Clamped at the latest bar on the right, because there is nothing past it, and
 * on the left at the first *screenful* rather than the first bar: stopping at
 * bar 0 would leave the whole window empty but for one candle at its right edge.
 *
 * `appliedGap` tells the window's last **bar** from its right edge, exactly as
 * {@link windowHoldingRight} needs it to — the margin is empty space inside
 * `max`, and panning by the edge would drift by the margin on every press.
 */
export function windowPannedBy(opts: {
  current: BarWindow;
  appliedGap: number;
  visibleBars: number;
  rightMargin?: number;
  deltaBars: number;
  /** Real bars loaded — a projection's rows are not somewhere to pan to. */
  barCount: number;
}): BarWindow {
  const { current, appliedGap, visibleBars, deltaBars, barCount } = opts;
  const rightMargin = opts.rightMargin ?? DEFAULT_RIGHT_MARGIN;
  const last = current.max - appliedGap;
  const latest = Math.max(0, barCount - 1);
  const earliest = Math.min(visibleBars - 1, latest);
  return windowEndingAt(Math.max(earliest, Math.min(latest, last + deltaBars)), visibleBars, rightMargin);
}

/**
 * The count the zoom buttons move to: two thirds of it in, half as much again
 * out, never below {@link MIN_VISIBLE_BARS} and never past what is loaded.
 *
 * A ratio rather than a step, because a step that reads well at 150 bars is
 * either invisible at 1500 or the whole window at 20. Zooming out stops at the
 * bars the chart holds rather than asking for more: a preset that needs history
 * raises `barsRange` and refetches, and a zoom button that quietly did the same
 * would make a wheel-sized gesture cost a round trip.
 */
export function zoomedBars(visibleBars: number, direction: "in" | "out", barCount: number): number {
  if (direction === "in") return Math.max(MIN_VISIBLE_BARS, Math.round((visibleBars * 2) / 3));
  // **Zoom out never returns fewer bars than are on screen.** The ceiling is
  // there to stop it *asking* for more than is loaded, and it used to be
  // applied flat — so a chart already wider than its own history (a recent
  // listing with 60 bars against the default 150, or any count typed into the
  // box) answered "show more bars" by showing 60, and `zoom` persisted that.
  // `windowEndingAt` supports a window wider than the data on purpose: the
  // empty space on the left is the honest edge of the history.
  const ceiling = Math.max(MIN_VISIBLE_BARS, barCount);
  return Math.max(visibleBars, Math.min(ceiling, Math.round(visibleBars * 1.5)));
}

/**
 * The window centred on one bar, never running past the last one.
 *
 * Centred rather than scrolled-to-start because a date you went looking for is a
 * date you want context either side of. The clamp is the exception: a date near
 * the live edge centres as far as it can and then stops, since empty space to the
 * right of today is not context.
 */
export function windowAround(
  index: number,
  barCount: number,
  visibleBars: number,
  rightMargin = DEFAULT_RIGHT_MARGIN,
): BarWindow {
  const lastBar = Math.min(barCount - 1, index + Math.floor(visibleBars / 2));
  return windowEndingAt(lastBar, visibleBars, rightMargin);
}

/**
 * The first bar at or after `timeSec`, or `x.length` when every bar is earlier.
 *
 * Binary search over the ascending timestamps the feed already delivers
 * (`bulkDeltas` in `lib/chart-feed.ts` maps them in), so "go to 2022" costs no
 * scan of five thousand bars per keystroke. Times are unix **seconds**, which is
 * what the transport decodes an Arrow timestamp column into.
 */
export function indexAtOrAfter(x: readonly number[], timeSec: number): number {
  let lo = 0;
  let hi = x.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (x[mid]! < timeSec) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** How far back a preset reaches, and whether the loaded bars already cover it. */
export interface BarsBack {
  /** Bars from `startSec` to the last loaded bar, inclusive. */
  bars: number;
  /** True when counted from real timestamps; false when estimated. */
  measured: boolean;
}

/**
 * How many bars reach back to `startSec`.
 *
 * **Counted when the loaded history covers it, estimated when it does not** —
 * and the estimate comes from the loaded bars' own density rather than from a
 * model of the trading session. A session model has to know the exchange's
 * hours, its holidays and its half-days to answer "how many 5-minute bars are in
 * seven months", and it is wrong on every one of them it does not know; the bars
 * already in hand answer the same question by measurement, at the same symbol
 * and the same interval. It only has to be close enough to decide whether the
 * request is over the ceiling — the server sends what it sends.
 *
 * `undefined` when there is nothing to measure: no bars, or one bar, which has
 * no density. Both mean the chart has not loaded yet, and a preset should be
 * unavailable rather than guessing.
 */
export function barsBackTo(
  x: readonly number[],
  startSec: number,
): BarsBack | undefined {
  const first = x[0];
  const last = x[x.length - 1];
  if (first === undefined || last === undefined) return undefined;

  if (first <= startSec) {
    return { bars: Math.max(1, x.length - indexAtOrAfter(x, startSec)), measured: true };
  }

  const seconds = last - first;
  if (x.length < 2 || seconds <= 0) return undefined;
  const perSecond = (x.length - 1) / seconds;
  return { bars: Math.ceil((last - startSec) * perSecond) + 1, measured: false };
}

/** The presets offered on the chart, and how far back each reaches. */
export type SpanPreset = "1y" | "ytd";

/** How a preset is spelled to a person. */
export const SPAN_LABELS: Record<SpanPreset, string> = {
  "1y": "1Y",
  ytd: "YTD",
};

/**
 * The first instant a preset wants on screen, in unix seconds.
 *
 * **UTC, like the bars.** A daily bar has no time of day — the server sends it
 * as a date, which arrives as a UTC-midnight timestamp — so a boundary computed
 * in the reader's local time is offset from the data by their whole timezone.
 * That is not a rounding error: west of Greenwich, local midnight on the 1st is
 * *after* the 1st's bar, so a span starting there silently drops the first day
 * it was asked for. Measured on a running chart, which is where it showed up.
 */
export function spanStart(preset: SpanPreset, now: Date): number {
  const y = now.getUTCFullYear();
  if (preset === "ytd") return Date.UTC(y, 0, 1) / 1000;
  return Date.UTC(y - 1, now.getUTCMonth(), now.getUTCDate()) / 1000;
}

/**
 * What a chart should do about a span: show it, load more first, or refuse.
 *
 * `barsRange` present means a refetch — `ChartGroupView` keys its rebuild on it,
 * so raising it is the whole mechanism and there is no second path to add.
 */
export interface WindowPlan {
  /** Bars to show. Absent when the plan is a refusal. */
  visibleBars?: number;
  /** Bars to load, set only when the span reaches past what is loaded. */
  barsRange?: number;
  /** Why the span cannot be honoured. Set only when it is refused. */
  refused?: string;
}

/** Thousands separators, so 22000 reads as a size rather than a serial number. */
const count = (n: number): string => Math.round(n).toLocaleString();

/**
 * Plan a jump back to `startSec`.
 *
 * The three answers, in the order they are decided:
 *
 * - **Loaded already** — set the window, send nothing. This is the common case
 *   and the reason 1500 bars are loaded for a 150-bar view.
 * - **Not loaded, and gettable** — raise `barsRange` to what the span needs and
 *   let the ordinary rebuild refetch. Clamping to what is loaded was the
 *   alternative and is the failure this codebase keeps choosing against: it
 *   gives three months to someone who asked for a year, and the chart looks
 *   correct while being wrong.
 * - **Not loaded, and over the ceiling** — refused, with the numbers in the
 *   message. YTD on a five-minute chart is more bars than the server will ever
 *   send, and a disabled control saying so beats an enabled one that lies.
 *
 * `barsRange` is raised to exactly what the span needs, never padded: extra
 * headroom is a fetch of bars nobody asked for. What is already loaded is kept,
 * so a span *inside* the current load never shrinks it.
 */
export function planSpan(opts: {
  /** The instant to reach back to, unix seconds. */
  startSec: number;
  /** Loaded bar timestamps, ascending, unix seconds. */
  x: readonly number[];
  /** The chart group's current `barsRange`. */
  barsRange?: number;
  /** How the span is named in a refusal — "YTD", "8 Mar 2021". */
  label: string;
  /** The chart's interval, named in a refusal so the reason is actionable. */
  frequency: string;
  maxBars?: number;
  /**
   * The load is every bar the symbol has (`qv.complete`, daily). A span that
   * reaches past its first bar then shows all of it: there is nothing further
   * back to fetch, and raising `barsRange` would refetch the same file.
   */
  complete?: boolean;
}): WindowPlan {
  const maxBars = opts.maxBars ?? MAX_LOADED_BARS;
  const back = barsBackTo(opts.x, opts.startSec);
  if (!back) return { refused: "waiting for bars" };

  if (back.measured) return { visibleBars: back.bars };
  // Before the refusal, not after it: a symbol listed thirty years ago has no
  // 22,000 bars to be refused, and the ceiling is about requests, of which a
  // complete load makes none.
  if (opts.complete) return { visibleBars: opts.x.length };

  if (back.bars > maxBars) {
    return {
      refused:
        `${opts.label} is about ${count(back.bars)} bars at ${opts.frequency}, ` +
        `and ${count(maxBars)} is the most one request returns`,
    };
  }

  return {
    visibleBars: back.bars,
    barsRange: Math.min(maxBars, Math.max(opts.barsRange ?? 0, back.bars)),
  };
}

/**
 * Plan a jump to one date, which is a span plus **half a window of context**.
 *
 * The difference from {@link planSpan} is the whole reason this exists rather
 * than the date box calling that directly. A preset ends at today, so the bars
 * it needs are the bars it shows; a date is *centred*, so half the window sits
 * to the left of it — and asking for exactly the span puts the date on the first
 * bar the server sent, with the left half of the chart empty. Measured on a
 * running chart, asking for 2019 on a chart loaded back to 2020.
 *
 * Never returns `visibleBars`: going to a date does not change how many bars are
 * on screen, only which ones.
 */
export function planDate(opts: {
  /** The date to centre on, unix seconds. */
  startSec: number;
  x: readonly number[];
  barsRange?: number;
  /** The window it will be centred in — half of it is the context to fetch. */
  visibleBars: number;
  label: string;
  frequency: string;
  maxBars?: number;
}): WindowPlan {
  const maxBars = opts.maxBars ?? MAX_LOADED_BARS;
  const span = planSpan(opts);
  if (span.refused !== undefined) return { refused: span.refused };
  if (span.barsRange === undefined) return {};
  return {
    barsRange: Math.min(maxBars, span.barsRange + Math.ceil(opts.visibleBars / 2)),
  };
}

/**
 * A date typed into the toolbar, as unix seconds — or `undefined` if it is not
 * one.
 *
 * Midnight **UTC**, matching {@link spanStart} and the bars themselves. Parsed
 * by hand rather than through `new Date(value)` even though that also reads a
 * bare `yyyy-mm-dd` as UTC: `Date` accepts a great deal more than a date box
 * ever produces, and this has to be able to say "that is not a date" for a
 * half-typed one.
 *
 * Local midnight was the first attempt and is visibly wrong: at UTC-7 it lands
 * seven hours *after* the requested day's own bar, so `indexAtOrAfter` skips to
 * the next one and the chart centres a day late. That is one bar out of a
 * hundred and fifty — invisible unless you count, which is why it is written
 * down here.
 */
export function parseDateInput(value: string): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return undefined;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? t / 1000 : undefined;
}
